import { useState } from 'react'
import type { Finding, FindingType } from '../types'
import { postIgnore, postRevise, postFeedback } from '../api'
import {
  evidenceQuotes,
  findingAction,
  findingImportance,
  findingSummary,
  recurringNote,
  splitLabeledText,
} from '../findings'
import { t } from '../i18n'
import {
  copyToClipboard,
  insertCommentAt,
  insertCounterEditAt,
  locateAndSelect,
} from '../word'

type ActionState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'ok'; message: string }
  | { kind: 'warn'; message: string }
  | { kind: 'error'; message: string }

interface Props {
  finding: Finding
  token: string
  reviewId: string | null
  commentInserted: boolean
  applied: boolean
  onCommentInserted: () => void
  onApplied: () => void
  onIgnored: () => void
}

const TYPE_LABEL: Record<FindingType, string> = {
  deviation: 'Відхилення',
  missing: 'Відсутнє положення',
  atypical: 'Нетипове',
  recurring: 'Повторювана',
}

export function FindingCard({
  finding,
  token,
  reviewId,
  commentInserted,
  applied,
  onCommentInserted,
  onApplied,
  onIgnored,
}: Props) {
  const options = (finding.replacement_options ?? [])
    .map((text) => text.trim())
    .filter(Boolean)
  const [why, setWhy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [chosen, setChosen] = useState(finding.replacement_text ?? options[0] ?? '')
  const [draft, setDraft] = useState(finding.replacement_text ?? options[0] ?? '')
  const [action, setAction] = useState<ActionState>({ kind: 'idle' })

  const quotes = evidenceQuotes(finding)
  const degraded =
    finding.degraded === true
    || (!Array.isArray(finding.evidence) && finding.evidence?.degraded === true)
    || finding.confidence === 0
  const canNavigate = (finding.anchor_text ?? '').trim().length > 0
  const canComment = finding.verdict !== 'ok' && (finding.justification ?? '').trim().length > 0
  const applySource = (editing ? draft : draft || chosen || finding.replacement_text || '').trim()
  const canApply = applySource.length > 0
  const insertMode = finding.insert_mode === 'insert' || finding.finding_type === 'missing'
    ? 'insert'
    : 'replace'
  const importance = findingImportance(finding)
  const actionKind = findingAction(finding)
  const locationLabel = finding.clause_ref
    ?? (finding.section_path
      ? t('вставка біля {section}', { section: finding.section_path })
      : t('без привʼязки'))
  const heading = finding.rule_title || locationLabel
  const clauseForRevise = finding.anchor_text || findingSummary(finding)

  async function onNavigate() {
    if (!canNavigate) return
    setAction({ kind: 'busy' })
    const ok = await locateAndSelect({
      anchorText: finding.anchor_text,
      occurrenceIndex: finding.occurrence_index,
    })
    setAction(
      ok
        ? { kind: 'idle' }
        : { kind: 'warn', message: t('Не вдалося знайти пункт — документ змінився?') },
    )
  }

  async function onInsertComment() {
    if (!canComment) return
    if (commentInserted) {
      const proceed = window.confirm(t('Коментар уже вставлено. Додати ще один?'))
      if (!proceed) return
    }
    setAction({ kind: 'busy' })
    const result = await insertCommentAt({
      anchorText: finding.anchor_text,
      occurrenceIndex: finding.occurrence_index,
      justification: finding.justification,
      findingType: finding.finding_type,
    })
    if (result.ok) {
      onCommentInserted()
      setAction({ kind: 'ok', message: t('Коментар вставлено') })
      return
    }
    const copied = await copyToClipboard(
      `[${t(TYPE_LABEL[finding.finding_type])}] ${finding.justification}`,
    )
    setAction(
      copied
        ? {
            kind: 'warn',
            message:
              result.reason === 'range-miss'
                ? t('Пункт не знайдено. Обґрунтування скопійовано в буфер.')
                : t('Не вдалося вставити коментар. Обґрунтування скопійовано в буфер.'),
          }
        : { kind: 'error', message: t('Не вдалося вставити коментар і скопіювати текст.') },
    )
  }

  async function applyText(text: string) {
    if (!text.trim()) return
    setAction({ kind: 'busy' })
    const result = await insertCounterEditAt({
      replacementText: text,
      insertMode,
    })
    if (result.ok) {
      onApplied()
      setEditing(false)
      const bySource =
        result.source === 'selection'
          ? t('Правку застосовано до виділеного фрагмента')
          : insertMode === 'insert'
            ? t('Текст вставлено новим абзацом у позицію курсора')
            : t('Вставлено в позицію курсора')
      setAction({ kind: 'ok', message: bySource })
      return
    }
    const copied = await copyToClipboard(text)
    setAction(
      copied
        ? {
            kind: 'warn',
            message: t('Не вдалося вставити. Формулювання скопійовано — вставте вручну.'),
          }
        : { kind: 'error', message: t('Не вдалося вставити правку.') },
    )
  }

  async function onAiRevise() {
    setAction({ kind: 'busy' })
    try {
      const result = await postRevise({
        token,
        clauseText: clauseForRevise,
        ruleId: finding.rule_id,
        justification: finding.justification,
        modelLanguage: chosen || finding.replacement_text,
        modelLanguageOptions: options,
      })
      setDraft(result.revised_text)
      setEditing(true)
      setAction({
        kind: 'ok',
        message: result.source === 'model'
          ? t('ШІ-правка готова — перевірте текст і натисніть «Застосувати»')
          : t('Підставлено формулювання з плейбука — перевірте й натисніть «Застосувати»'),
      })
    } catch (err) {
      setAction({
        kind: 'error',
        message: err instanceof Error ? err.message : t('Не вдалося згенерувати правку.'),
      })
    }
  }

  async function onIgnore() {
    setAction({ kind: 'busy' })
    if (finding.rule_id) {
      try {
        await postIgnore({ token, ruleId: finding.rule_id, reviewId })
      } catch (err) {
        console.warn('ignore persist failed', err)
      }
    }
    onIgnored()
    setAction({ kind: 'ok', message: t('Сховано до кінця цієї перевірки') })
  }

  return (
    <li className={`card finding finding-type-${finding.finding_type} finding-imp-${importance}`}>
      <div className="finding-head">
        <button
          type="button"
          className="linkish clause-ref"
          onClick={() => void onNavigate()}
          disabled={!canNavigate}
          title={
            finding.finding_type === 'missing'
              ? t('Рекомендоване місце вставки (не точна локація)')
              : canNavigate
                ? t('Показати в документі')
                : t('Пункт без прив’язки')
          }
        >
          {heading}
        </button>
        <span className={`badge badge-action-${actionKind}`}>
          {actionKind === 'addition' ? t('Додавання') : t('Правка')}
        </span>
        <span className={`badge badge-imp-${importance}`}>
          {importance === 'high' ? t('Висока') : importance === 'medium' ? t('Середня') : t('Низька')}
        </span>
      </div>

      {recurringNote(finding) ? (
        <p className="muted small">{recurringNote(finding)}</p>
      ) : null}

      {finding.finding_type === 'missing' ? (
        <p className="muted small">
          {t('Поставте курсор у потрібну колонку. Текст з’явиться новим абзацом у цій точці.')}
        </p>
      ) : (
        <p className="muted small">
          {t('Виділіть текст, щоб замінити його, або поставте курсор, щоб вставити. Клік по назві пункту виділяє його в документі.')}
        </p>
      )}

      <LabeledText className="change-summary" text={findingSummary(finding)} />

      {quotes.map((quote, i) => (
        <blockquote key={i}>{quote}</blockquote>
      ))}

      {why ? (
        <LabeledText className="justification" text={finding.justification} />
      ) : finding.justification ? (
        <button type="button" className="linkish" onClick={() => setWhy(true)}>{t('Показати деталі')}</button>
      ) : null}

      {options.length > 1 ? (
        <label className="clause-pick">
          <span className="muted small">{t('Затверджене положення')}</span>
          <select
            aria-label={t('Затверджене положення')}
            value={options.includes(chosen) ? chosen : options[0]}
            onChange={(e) => {
              setChosen(e.target.value)
              setDraft(e.target.value)
            }}
          >
            {options.map((text, idx) => (
              <option key={`${idx}:${text.slice(0, 24)}`} value={text}>
                {text.length > 80 ? `${text.slice(0, 80)}…` : text}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {editing ? (
        <textarea
          className="edit-draft"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          aria-label={t('Текст правки')}
        />
      ) : null}

      {applied ? (
        <p className="muted small">
          {t('Правку вже вставляли в цій сесії. Повторне «Застосувати» вставить її туди, де зараз курсор.')}
        </p>
      ) : null}

      <div className="finding-actions">
        {canApply ? (
          <div className="finding-actions-primary">
            <button
              type="button"
              onClick={() => void applyText(applySource)}
              disabled={action.kind === 'busy'}
            >{t('Застосувати')}</button>
          </div>
        ) : null}
        <div className="finding-actions-secondary">
          {canComment ? (
            <button
              type="button"
              className="secondary"
              onClick={() => void onInsertComment()}
              disabled={action.kind === 'busy'}
            >
              {commentInserted ? t('Ще коментар') : t('Коментар')}
            </button>
          ) : null}
          <button
            type="button"
            className="secondary"
            onClick={() => void onIgnore()}
            disabled={action.kind === 'busy'}
          >{t('Ігнорувати')}</button>
        </div>
        <div className="finding-actions-tools">
          <button
            type="button"
            className="linkish"
            onClick={() => void onAiRevise()}
            disabled={action.kind === 'busy'}
          >{t('ШІ-правка')}</button>
          {canApply || finding.replacement_text ? (
            <button
              type="button"
              className="linkish"
              onClick={() => {
                setDraft(draft || chosen || finding.replacement_text || '')
                setEditing((v) => !v)
              }}
              disabled={action.kind === 'busy'}
            >
              {editing ? t('Сховати редактор') : t('Редагувати')}
            </button>
          ) : null}
        </div>
      </div>

      {reviewId ? <div className="finding-actions-secondary">
        {([['correct', t('Правильне зауваження')], ['false_positive', t('Хибне зауваження')]] as const).map(([label, title]) => (
          <button key={label} type="button" className="secondary" onClick={async () => {
            try { await postFeedback(token, reviewId, {rule_id: finding.rule_id, label});
              setAction({kind: 'ok', message: t('Відгук збережено')}) }
            catch { setAction({kind: 'error', message: t('Не вдалося зберегти відгук')}) }
          }}>{title}</button>
        ))}
      </div> : null}
      {action.kind === 'ok' ? <p className="ok">{action.message}</p> : null}
      {action.kind === 'warn' ? <p className="warn">{action.message}</p> : null}
      {action.kind === 'error' ? <p className="error">{action.message}</p> : null}

      {degraded ? (
        <p className="degraded finding-meta">{t('Потребує ручної перевірки')}</p>
      ) : (
        <p className="muted small finding-meta">
          {finding.evidence_basis === "full_document_scan" ? t('Перевірено весь доступний текст') : t('Цитати перевірено за текстом договору')}
        </p>
      )}
    </li>
  )
}

function LabeledText({ text, className }: { text: string; className?: string }) {
  const parts = splitLabeledText(text)
  return (
    <p className={className}>
      {parts.map((part, i) => {
        if (part.kind === 'text') {
          return <span key={i}>{part.value}</span>
        }
        const prev = i > 0 ? parts[i - 1] : null
        const breakBefore = prev != null && !prev.value.endsWith('\n')
        return (
          <strong key={i} className="text-label">
            {breakBefore ? '\n' : ''}
            {part.value}
          </strong>
        )
      })}
    </p>
  )
}
